/**
 * Extracts the parts of the file name and determines the latest numeric suffix.
 *
 * This function breaks down the file name into its component parts:
 * - Base name: The main part of the file name, excluding all trailing numeric suffixes in parentheses
 * - Extension: The file extension, including the dot
 * - Count: The last numeric suffix found in the file name
 *
 * It handles cases where the file name might have multiple numeric suffixes in parentheses,
 * removing all of them from the base name. Non-numeric values in parentheses that are not at
 * the end of the base name are retained as part of the base name.
 *
 * @param origFileName - The file name.
 * @returns An object containing the extracted file name parts and the last numeric suffix.
 *
 * @example
 * const parts = extractOrigFileNameParts('my (1)(2) image (3)(4)(5).jpg');
 * // Returns: { baseName: 'my (1)(2) image', extension: '.jpg', count: 5 }
 */
export const extractOrigFileNameParts = (origFileName: string = '') => {
  const match = String(origFileName).match(
    /^(.*?)(?:\s*\((\d+)\))*(\.[^.]+)?$/
  );
  if (!match) {
    throw new Error(
      `Invalid file name: ${origFileName}. Unable to extract file name parts.`
    );
  }

  const [, baseName = '', lastNumber = '', extension = ''] = match;
  const count = lastNumber ? parseInt(lastNumber, 10) : 0;

  // Remove all trailing (numbers) from the baseName
  const cleanBaseName = baseName.replace(/\s*\(\d+\)*$/, '').trim();

  return {
    baseName: cleanBaseName,
    extension,
    count,
  };
};
